/**
 * Search App — config-driven block data for the branded /search route.
 *
 * The `/search` route (customizations/components/theme/Search/Search.jsx) hosts
 * ONE stock-shaped Volto search block, rendered through the addon's branded
 * SearchBlockView (which already dispatches to `@querystring-search` — the
 * backend Solr swap-seam). So the whole "search app" is: this config object +
 * the route wrapper + the pieces the browse pages already use (SearchInput /
 * Facets / SortOn / TableListing / search.less).
 *
 * WHERE TO CHANGE THINGS. The searchable types, facets, columns, page size and
 * site list are NO LONGER declared here — they live in the repo-root
 * `search-registry.json`, the single source of truth shared with the backend
 * (`search/config.py`) and the eval harness (`scripts/search_eval/score_search.py`).
 * Edit that file and re-run:
 *
 *     python3 scripts/gen_search_registry.py
 *
 * which rewrites `searchRegistry.generated.ts` (imported below). This module
 * keeps only what is genuinely frontend-shaped: the Volto block wiring, and the
 * rationale comments for decisions that are invisible in a data file.
 *
 * Why the split: these facts used to be hand-copied into three files and they
 * drifted — the eval harness scored `file_text^2` for weeks after the backend
 * shipped `^10`, and was missing three of the nine searchable types, so its
 * numbers measured a ranking the app never served.
 */

import {
  SEARCH_TYPES,
  SEARCH_PAGE_SIZE,
  SITE_CHOICES,
  SEARCH_FACETS,
  SEARCH_COLUMN_CATALOG,
} from './searchRegistry.generated';

/**
 * Document types the search app ranks — from the registry's `search_types`.
 * Deliberately the DOCUMENT families (the moat): the redundant `File` PDF
 * objects are excluded (the v3 indexer folds each PDF's inner text onto its
 * parent document, so the document itself matches text INSIDE its PDF).
 *
 * Glossary + people (Definition / Acronym / Person) were added 2026-07-22 on
 * measurement. They were fully findable in Solr and completely invisible in the
 * app: `glossary_term`, `acronym` and `person` all scored P@1 0.000 here and
 * 1.000 against the index. The concern was that 3,779 such objects would dilute
 * 9,505 documents — measured, and they do not: adding them moved the headline
 * 0.730 -> 0.844 (n=392) with NO regression in any document class (id_exact,
 * id_variant, title_exact, title_partial, inner_phrase, morphology,
 * natural_language, typo all unchanged). The only cost is set-class first-page
 * purity (`subject` 0.198 -> 0.133), a browse-intent metric that curated browse
 * pages address properly.
 *
 * NOTE: `portal_type` is intentionally a HARD BASE FILTER, not a facet — Volto's
 * `normalizeState` strips any base-query criterion whose index is also a
 * configured facet, which would re-admit `File`. That is why the registry marks
 * portal_type `facet: true` (Solr counts it) but `ui: null` (never rendered as a
 * facet widget), and why the generator refuses to emit it into SEARCH_FACETS.
 */
export { SEARCH_TYPES };

/** results per page. */
export { SEARCH_PAGE_SIZE };

const ANY = 'plone.app.querystring.operation.selection.any';

/**
 * The archives the cross-site Archive facet can span. Static, config-driven
 * choices (the backend has no `site` catalog index, so Volto can't source these
 * from a querystring vocabulary) — injected into the facet via `rewriteOptions`
 * + the custom `archiveFacet` widget type registered in index.ts. Values match
 * the Solr `site` field / backend `SITE_LABELS` BY CONSTRUCTION now: both sides
 * read the registry's `sites`. Add a legacy archive.*.doxcelerate.com site
 * there (and index it) to make it searchable — zero component change.
 * Selecting nothing = the current site only (the seam's default scope);
 * selecting archives searches exactly those.
 */
export { SITE_CHOICES };

/**
 * Facet widgets (left rail), in display order. Generated from the registry's
 * `ui_facet_order` + each field's `ui` type. Every entry is guaranteed by the
 * generator to be a field the backend seam will actually push to Solr as an
 * `fq` (registry `filter: true`) — a facet that filters nothing can no longer
 * be configured by accident. type = selectFacet | checkboxFacet |
 * daterangeFacet | archiveFacet.
 */
export { SEARCH_FACETS };

/**
 * FULL result-column CATALOG (the AWS-table-style column picker's option list),
 * from the registry's `columns`. `field` is a metadata column returned on each
 * hit (same-site hits serialize with `metadata_fields:_all`; cross-site hits
 * carry the curated set from the seam's `_solr_item`, incl. `site_label`), so a
 * picked column populates on rows from any archive. `default:true` = shown on
 * first load; the rest are opt-in via the "Columns" dropdown in the results
 * table. `format`: 'status' -> brand pill, 'date' -> formatted date.
 */
export { SEARCH_COLUMN_CATALOG };

/** Columns shown on first load (the default-visible subset of the catalog). */
export const SEARCH_COLUMNS = SEARCH_COLUMN_CATALOG.filter((c) => c.default);

/**
 * A catalog entry -> the normalized field-config the listing table reads.
 *
 * `field` is always widened to an array because `TableListing` reads it that
 * way. A catalog entry that declares a list keeps it, along with its `resolve`
 * mode: without `resolve` the table stacks one line per field (the legacy
 * Delegate/DelegatePos pairing), with `resolve: 'first'` it renders the first
 * field that has a value. The default date column uses the latter — see
 * `$comment_columns` in search-registry.json (BL-0057).
 */
const toFieldConfig = (c: {
  field: string | string[];
  resolve?: 'first';
  title: string;
  link?: boolean;
  format?: string;
}) => {
  const names = Array.isArray(c.field) ? c.field : [c.field];
  return {
    // Distinct per column: a list-valued field joins on ',', so the combined
    // date column and a standalone `authority_effective_date` column cannot
    // collide on one id.
    '@id': `col-${names.join(',')}`,
    field: names,
    title: c.title,
    link: !!c.link,
    ...(c.resolve ? { resolve: c.resolve } : {}),
    ...(c.format ? { format: c.format } : {}),
  };
};

/**
 * Build the search block `data` the route renders. Deterministic ids (no random)
 * so server + client markup match under SSR.
 */
export type SearchAppControls = {
  /** 'search' (the tool) or 'browse' (a category surface). See item 1. */
  mode?: string;
  /** Current | Archive | All. Omitted => the seam applies its own default and
   *  reports it back in search_meta, which is what makes the chip removable. */
  statusGroup?: string;
  /** AND | OR. Omitted => the seam's default (OR). */
  operator?: string;
  /** item 21: the literal query text to leave UNCORRECTED. Term-bound on
   *  purpose, so it expires the moment the user searches for something else —
   *  see components/Search/SearchCorrection. */
  correct?: string;
};

export function getSearchAppData(controls: SearchAppControls = {}): any {
  const { mode = 'search', statusGroup, operator, correct } = controls;
  return {
    '@type': 'search',
    variation: 'facetsLeftSide',
    // Explicit intent (item 1). Without this the seam has to guess from whether
    // a term is present, and an empty search box falls through to the stock
    // catalog — no Solr ranking, no cross-site scope, no facet counts.
    mode,
    // Only sent when the user has actually chosen. Leaving them undefined is
    // what lets the backend apply its measured defaults AND report them in
    // search_meta so the UI can show them as removable chips (item 2).
    ...(statusGroup ? { status_group: statusGroup } : {}),
    ...(operator ? { operator } : {}),
    // The search app POSTs its own request, so the browser's URL never reaches
    // the seam — a control param only takes effect if it rides in the body.
    ...(correct ? { correct } : {}),
    // base query: restrict to the document families. No SearchableText here —
    // the user's term comes in via the SearchInput / ?SearchableText= URL param.
    query: {
      query: [{ i: 'portal_type', o: ANY, v: SEARCH_TYPES }],
      b_size: SEARCH_PAGE_SIZE,
      sort_on: '',
      sort_order: '',
    },
    facets: SEARCH_FACETS.map((f) => ({
      '@id': `facet-${f.field}`,
      field: { label: f.label, value: f.field },
      title: f.label,
      type: f.type,
      multiple: !!f.multiple,
      hidden: false,
    })),
    fields: SEARCH_COLUMNS.map(toFieldConfig),
    // Full catalog for the results table's "Columns" multi-select (AWS-style).
    // TableListing reads this to render the picker; `fields` above is the
    // initial visible set. Passed straight through ListingBody's {...data}.
    columnCatalog: SEARCH_COLUMN_CATALOG.map(toFieldConfig),
    listingBodyTemplate: 'cards-id',
    showSearchInput: true,
    // Placeholder for the in-block SearchInput (see the SearchInput shadow,
    // which renders the real box only for this block id).
    searchInputPrompt:
      'Search by title, document number, or text inside the PDF…',
    showSortOn: true,
    showTotalResults: true,
  };
}

export default getSearchAppData;
